import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { industryIcon } from "@/lib/industryIcon";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, FileText, Trophy, DollarSign, Loader2, ExternalLink, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const nf = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatCard({ icon: Icon, label, value, sub, tint }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tint}`}><Icon className="h-4.5 w-4.5" /></span>
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{value}</div>
      {sub ? <div className="mt-1 text-sm text-muted-foreground">{sub}</div> : <div className="mt-1 text-sm">&nbsp;</div>}
    </div>
  );
}

export default function IndustryDetail() {
  const { value } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState("");
  const [cur, setCur] = useState("TZS");

  useEffect(() => {
    api.get(`/industries/${value}/detail`).then((r) => { setData(r.data); setLabel(r.data.label); }).catch(() => setData({ error: true }));
  }, [value]);

  const saveRename = async () => {
    if (!label.trim()) { toast.error("Name can't be empty."); return; }
    try {
      await api.put(`/industries/${value}`, { label: label.trim() });
      setData((d) => ({ ...d, label: label.trim() }));
      setEditing(false); toast.success("Industry renamed");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <AppLayout title="Industry"><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppLayout>;
  if (data.error) return <AppLayout title="Industry"><div className="py-20 text-center text-sm text-muted-foreground">Industry not found.</div></AppLayout>;

  const t = data.totals;
  const rate = data.rate || 0;
  const conv = t.quoted_tzs > 0 ? `${Math.round((t.won_tzs / t.quoted_tzs) * 100)}% conversion` : null;
  const show = (tzs) => (cur === "TZS" ? `TZS ${nf(tzs)}` : `USD ${nf(rate ? tzs / rate : 0)}`);
  const cell = (tzs) => (tzs ? show(tzs) : "—");
  const Icon = industryIcon(value || data.label);

  return (
    <AppLayout title={data.label}>
      <button onClick={() => navigate("/industries")} className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary" data-testid="back-to-industries">
        <ArrowLeft className="h-4 w-4" /> Back to Industries
      </button>

      <div className="mb-6 flex items-center gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E6ECF5] text-[#1B4383]"><Icon className="h-6 w-6" /></span>
        <div className="flex-1">
          <div className="text-xs font-semibold tracking-wide text-brand-soft">Industry</div>
          {editing ? (
            <div className="mt-1 flex items-center gap-2">
              <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") { setEditing(false); setLabel(data.label); } }}
                className="h-9 max-w-sm" data-testid="industry-rename-input" />
              <Button size="sm" variant="ghost" onClick={saveRename} data-testid="industry-rename-save"><Check className="h-4 w-4 text-ok" /></Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setLabel(data.label); }}><X className="h-4 w-4 text-muted-foreground" /></Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900" data-testid="industry-detail-name">{data.label}</h2>
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)} title="Rename industry" data-testid="industry-rename-btn"><Pencil className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 flex items-center justify-end">
        <div className="inline-flex rounded-lg border border-border bg-white p-0.5" data-testid="industry-currency-toggle">
          {["TZS", "USD"].map((c) => (
            <button key={c} onClick={() => setCur(c)} data-testid={`industry-cur-${c}`}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${cur === c ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{c}</button>
          ))}
        </div>
      </div>
      <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={DollarSign} label="Quoted Value" value={show(t.quoted_tzs)} tint="bg-[#E6ECF5] text-[#1B4383]" />
        <StatCard icon={Trophy} label="Won Value" value={show(t.won_tzs)} sub={conv} tint="bg-emerald-50 text-emerald-600" />
        <StatCard icon={Users} label="Clients" value={t.clients} tint="bg-slate-100 text-slate-600" />
        <StatCard icon={FileText} label="RFQs" value={t.rfqs} sub={`${t.won} won`} tint="bg-amber-50 text-amber-600" />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-white">
        <div className="border-b border-border px-5 py-3 font-heading font-semibold text-slate-900">Clients in {data.label}</div>
        {data.clients.length === 0 ? (
          <div className="py-14 text-center text-sm text-muted-foreground">No clients in this industry yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-slate-50 text-left text-muted-foreground">
              <tr>
                <th className="px-5 py-3 font-medium">Client</th>
                <th className="px-5 py-3 font-medium">RFQs</th>
                <th className="px-5 py-3 text-right font-medium">Quoted</th>
                <th className="px-5 py-3 text-right font-medium">Won</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid={`industry-client-${c.id}`}>
                  <td className="px-5 py-3">
                    <button onClick={() => navigate(`/clients/${c.id}`, { state: { fromIndustry: { value, label: data.label } } })} className="inline-flex items-center gap-1.5 font-medium text-brand underline-offset-2 hover:underline">
                      {c.name || "Unnamed client"}<ExternalLink className="h-3.5 w-3.5 opacity-70" />
                    </button>
                  </td>
                  <td className="px-5 py-3 text-slate-600">{c.rfqs}</td>
                  <td className="px-5 py-3 text-right font-medium text-slate-900">{cell(c.quoted_tzs)}</td>
                  <td className="px-5 py-3 text-right font-medium text-emerald-700">{cell(c.won_tzs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}
